import { createContext, useContext, useState, useMemo } from 'react';
import * as React from 'react';

import { TabType } from './types/index';

type SettingErrors = {
  [key in TabType]: string[];
};

export type SettingsErrorsContextType = {
  toggleError: (tab: TabType, fieldName: string, hasError: boolean) => void;
  isFieldValid: (fieldName: string) => boolean;
  isTabValid: (tab: TabType) => boolean;
  isSettingsValid: () => boolean;
  clearErrors: () => void;
  settingsErrors: SettingErrors;
  removeErrors: (tab: TabType, errors: string[]) => void;
};

const SettingsErrorsContext = createContext<SettingsErrorsContextType>({
  toggleError: null,
  isFieldValid: null,
  isTabValid: null,
  isSettingsValid: null,
  clearErrors: null,
  settingsErrors: null,
  removeErrors: null,
});

const initialErrorsState: SettingErrors = {
  [TabType.preferences]: [],
  [TabType.notifications]: [],
  [TabType.companySettings]: [],
  [TabType.schedule]: [],
  [TabType.timeBlocks]: [],
  [TabType.timeClock]: [],
  [TabType.laborCost]: [],
  [TabType.timeOff]: [],
  [TabType.integrations]: [],
};

const SettingsErrorsProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [settingsErrors, setSettingsErrors] = useState(initialErrorsState);

  const value = useMemo(() => {
    const toggleError = (tab: TabType, fieldName: string, hasError: boolean): void => {
      const errorIndex = settingsErrors[tab].indexOf(fieldName);
      if (!hasError) {
        if (errorIndex >= 0) {
          const errorsTabsCopy = [...settingsErrors[tab]];
          errorsTabsCopy.splice(errorIndex, 1);
          setSettingsErrors({
            ...settingsErrors,
            [tab]: errorsTabsCopy,
          });

          return;
        }

        return;
      }

      if (errorIndex === -1) {
        setSettingsErrors({
          ...settingsErrors,
          [tab]: [...settingsErrors[tab], fieldName],
        });

        return;
      }

      return;
    };

    const isFieldValid = (fieldName: string): boolean => {
      for (const property in settingsErrors) {
        if (settingsErrors[property].includes(fieldName)) {
          return false;
        }
      }

      return true;
    };

    const isTabValid = (tab: TabType): boolean => {
      return Boolean(!settingsErrors[tab]?.length);
    };

    const isSettingsValid = (): boolean => {
      for (const property in settingsErrors) {
        if (settingsErrors[property].length) return false;
      }

      return true;
    };

    const clearErrors = () => setSettingsErrors(initialErrorsState);

    const removeErrors = (tab: TabType, removedErrors: string[]) => {
      const updatedErrorsTab = settingsErrors[tab].filter(
        (tabError) => !removedErrors.includes(tabError)
      );

      setSettingsErrors({
        ...settingsErrors,
        [tab]: updatedErrorsTab,
      });
    };

    return {
      toggleError,
      isFieldValid,
      isTabValid,
      isSettingsValid,
      clearErrors,
      settingsErrors,
      removeErrors,
    };
  }, [settingsErrors]);

  return <SettingsErrorsContext.Provider value={value}>{children}</SettingsErrorsContext.Provider>;
};

function useSettingsErrorsContext(): SettingsErrorsContextType {
  const context = useContext(SettingsErrorsContext);
  if (!context) {
    throw new Error('useSettingsErrorsContext must be used within a SettingsErrorsProvider');
  }

  return context;
}

export { SettingsErrorsProvider, useSettingsErrorsContext };
